import { combineReducers } from 'redux';

import { createAccountEmailVerificationReducer } from '@/redux/modules/account/createAccountEmailVerification/createAccountEmailVerification.reducer';
import { loginReducer } from '@/redux/modules/account/login/login.reducer';
import { emailListReducer } from '@/redux/modules/account/notifications/emailList/emailList.reducer';
import { bankAccountReducer } from '@/redux/modules/account/payments/bankAccount/bankAccount.reducer';
import { bidderCardStatusReducer } from '@/redux/modules/account/payments/creditCard/cardStatus/bidderCardStatus.reducer';
import { creditCardReducer } from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.reducer';
import { shouldLoginReducer } from '@/redux/modules/account/shouldLogin/shouldLogin.reducer';
import { twoStepAuthenticationReducer } from '@/redux/modules/account/twoStepAuthentication/twoStepAuthentication.reducer';
import updateAccount from '@/redux/modules/account/updateAccount/updateAccount';
import { userReducer } from '@/redux/modules/account/user/user.reducer';
import { reducer as bidderRecentBidCount } from '@/redux/modules/bidder/recentBidCount/bidderRecentBidCount.reducer';
import { catalogAdReducer } from '@/redux/modules/catalog/ad/catalogAd.reducer';
import { catalogReducer } from '@/redux/modules/catalog/catalogs/catalog.reducer';
import { chairishSimilarItemsReducer } from '@/redux/modules/chairishSimilarItems/chairishSimilarItems.reducer';
import { changePasswordReducer } from '@/redux/modules/changePassword/changePassword.reducer';
import geoArea from '@/redux/modules/geoArea/geoArea';
import { savedItemsReducer } from '@/redux/modules/item/saved/savedItems.reducer';
import { itemCarouselReducer } from '@/redux/modules/itemCarousel/itemCarousel.reducer';
import { shippingQuoteReducer } from '@/redux/modules/shippingQuote/shippingQuote.reducer';
import { similarItemAlertsReducer } from '@/redux/modules/similarItemAlerts/similarItemAlerts.reducer';

import { absenteeBidReducer } from './modules/absenteeBid/absenteeBid.reducer';
import { createAccountReducer } from './modules/account/createAccount/createAccount.reducer';
import { deleteAccountReducer } from './modules/account/deleteAccount/deleteAccount.reducer';
import { reducer as analytics } from './modules/analytics';
import apiPerformanceStats from './modules/apiPerformanceStats';
import { articleReducer } from './modules/article/article.reducer';
import { articleLinksReducer } from './modules/articleLinks/articleLinks.reducer';
import { articleRelatedItemsReducer } from './modules/articleRelatedItems/articleRelatedItems.reducer';
import { auctionCalendarReducer } from './modules/auctionCalendar/auctionCalendar.reducer';
import { auctionsNearMeReducer } from './modules/auctionsNearMe/auctionsNearMe.reducer';
import { autoPopModalReducer } from './modules/autoPopModal/autoPopModal.reducer';
import bidCountDown from './modules/bidCountDown';
import { bidderBiddingInfoAllReducer } from './modules/bidder/biddingInfoAll/bidderBiddingInfoAll.reducer';
import { bidderExistsReducer } from './modules/bidder/exists/bidderExists.reducer';
import { bidderPreferencesReducer } from './modules/bidder/preferences/bidderPreferences.reducer';
import bidHistory from './modules/bidHistory';
import { bidIncrementsReducer } from './modules/bidIncrements/bidIncrements.reducer';
import { bidLimitReducer } from './modules/bidLimit/bidLimit.reducer';
import bidLimitExceeded from './modules/bidLimitExceeded';
import { bottomCardReducer } from './modules/bottomCard/bottomCard.reducer';
import { browsePathReducer } from './modules/browsePath/browsePath.reducer';
import browser from './modules/browser';
import { catalogAnnouncementReducer } from './modules/catalog/announcement/catalogAnnouncement.reducer';
import { catalogDetailReducer } from './modules/catalog/detail/catalogDetail.reducer';
import { catalogItemsReducer } from './modules/catalog/items/catalogItems.reducer';
import { catalogRegistrationReducer } from './modules/catalog/registration/catalogRegistration.reducer';
import { categoryReducer } from './modules/category/category.reducer';
import categoryCatalogCards from './modules/categoryCatalogCards';
import categoryRelatedSearches from './modules/categoryRelatedSearches';
import { reducer as certCapture } from './modules/certCapture';
import { checkoutReducer } from './modules/checkout/checkout.reducer';
import { checkoutShippingQuoteReducer } from './modules/checkoutShippingQuote/checkoutShippingQuote.reducer';
import { clientReducer } from './modules/client';
import combinationCategory from './modules/combinationCategory';
import completeAccount from './modules/completeAccount';
import config from './modules/config';
import { confirmPasswordReducer } from './modules/confirmPassword/confirmPassword.reducer';
import { consentReducer } from './modules/consent/consent.reducer';
import consignment from './modules/consignment';
import console from './modules/console';
import consoleBranding from './modules/consoleBranding';
import consoleMessages from './modules/consoleMessages';
import { conversationReducer } from './modules/conversation/conversations/conversation.reducer';
import { conversationFoldersReducer } from './modules/conversation/folders/conversationFolders.reducer';
import { conversationMessagesReducer } from './modules/conversation/messages/conversationMessages.reducer';
import cookies from './modules/cookies';
import curatedCollection from './modules/curatedCollection';
import currency from './modules/currency';
import discover from './modules/discover';
import errors from './modules/errors';
import { favoriteItemsReducer } from './modules/favorites/favorites.reducer';
import featuredCatalogs from './modules/featuredCatalogs';
import featuredCategories from './modules/featuredCategories';
import feedback from './modules/feedback';
import { followSeller } from './modules/followSeller';
import forgotPassword from './modules/forgotPassword';
import headerSearchDropdown from './modules/headerSearchDropdown';
import healthcheck from './modules/healthcheck';
import { helpModalReducer } from './modules/helpModalSlice/helpModalSlice.reducer';
import { homeReducer } from './modules/home/home.reducer';
import intl from './modules/intl';
import { itemSummaryReducer } from './modules/item/summary/itemSummary.reducer';
import { userBidPlacedItemsSliceReducer } from './modules/item/userBidPlacedItems/userBidPlacedItems.reducer';
import itemDetail from './modules/itemDetail';
import itemFacets from './modules/itemFacets';
import itemRemovalResponse from './modules/itemRemovalResponse';
import landingPage from './modules/landingPage';
import listingAgent from './modules/listingAgent';
import liveBid from './modules/liveBid';
import liveCatalogStatus from './modules/liveCatalogStatus';
import liveItemStatus from './modules/liveItemStatus';
import makeOffer from './modules/makeOffer';
import modal from './modules/modal';
import notification from './modules/notification';
import otherCategories from './modules/otherCategories/otherCategories';
import { paginationCookiesReducer, paginationLocalStorageReducer } from './modules/pagination';
import passwordReset from './modules/passwordReset';
import payment from './modules/payment';
import { phoneVerifyReducer } from './modules/phoneVerify/phoneVerify.reducer';
import { priceResultsReducer } from './modules/priceResults/priceResults.reducer';
import processingFee from './modules/processingFee';
import recentItem from './modules/recentItem';
import recommendedItem from './modules/recommendedItem';
import { recommendedSimilarItemsReducer } from './modules/recommendedSimilarItems';
import registerForCatalog from './modules/registerForCatalog';
import { reducer as reseller } from './modules/reseller';
import { retractBidReducer } from './modules/retractBid/retractBid.reducer';
import review from './modules/review';
import reviewStatus from './modules/reviewStatus';
import savedItemCount from './modules/savedItemCount';
import { savedSearch } from './modules/savedSearch';
import saveSearch from './modules/saveSearch';
import { categoryRelatedSearchTilesReducer } from './modules/search/categoryRelatedSearches/categoryRelatedSearches.reducer';
import { searchExclusionsReducer } from './modules/search/exclusions/searchExclusions.reducer';
import { imageSearchReducer } from './modules/search/imageSearch/imageSearch.reducer';
import { relatedSearchesReducer } from './modules/search/relatedSearches/relatedSearches.reducer';
import { searchReducer } from './modules/search/search.reducer';
import searchPromoted from './modules/searchPromoted';
import { searchSuggestionsReducer } from './modules/searchSuggestions/searchSuggestions.reducer';
import { sellerReducer } from './modules/seller/seller.reducer';
import sellerCatalogCounts from './modules/sellerCatalogCounts';
import sellerDetail from './modules/sellerDetail';
import sellerEndedCatalogs from './modules/sellerEndedCatalogs';
import sellerFollowerCount from './modules/sellerFollowerCount';
import sellerPreviewCatalogs from './modules/sellerPreviewCatalogs';
import sellerRatings from './modules/sellerRatings';
import sellerRecordResults from './modules/sellerRecordResults';
import sellerUpcomingCatalogs from './modules/sellerUpcomingCatalogs';
import sendSellerMessage from './modules/sendSellerMessage';
import setPassword from './modules/setPassword';
import { shippingReducer } from './modules/shipping/shipping.reducer';
import signifyd from './modules/signifyd';
import similarItems from './modules/similarItems';
import similarItemsCategories from './modules/similarItemsCategories';
import { similarItemsGroupsReducer } from './modules/similarItemsGroups';
import smsAlerts from './modules/smsAlerts';
import { storefrontReducer } from './modules/storefront/storefront.reducer';
import subcategoryTiles from './modules/subcategoryTiles';
import suggestedCategoryTiles from './modules/suggestedCategoryTiles';
import taxes from './modules/taxes';
import topRatedSellers from './modules/topRatedSellers';
import trustMetrics from './modules/trustMetrics';
import { upcomingItemsReducer } from './modules/upcoming/items/upcomingItems.reducer';
import upcomingCatalogs from './modules/upcomingCatalogs';
import video from './modules/video';
import viewData from './modules/viewData';
import waitingForApproval from './modules/waitingForApproval';
import whiteLabel from './modules/whiteLabel';
import windowSize from './modules/windowSize';
import { wonItemsReducer } from './modules/wonItems/wonItems.reducer';
import { wonItemsCountReducer } from './modules/wonItemsCount/wonItemsCount.reducer';

export default () =>
    combineReducers({
        absenteeBid: absenteeBidReducer,
        analytics,
        apiPerformanceStats,
        article: articleReducer,
        articleLinks: articleLinksReducer,
        articleRelatedItems: articleRelatedItemsReducer,
        auctionCalendar: auctionCalendarReducer,
        auctionsNearMe: auctionsNearMeReducer,
        autoPopModal: autoPopModalReducer,
        bankAccount: bankAccountReducer,
        bidCountDown,
        bidderBiddingInfoAll: bidderBiddingInfoAllReducer,
        bidderCardStatus: bidderCardStatusReducer,
        bidderExists: bidderExistsReducer,
        bidderPreferences: bidderPreferencesReducer,
        bidderRecentBidCount,
        bidHistory,
        bidIncrements: bidIncrementsReducer,
        bidLimit: bidLimitReducer,
        bidLimitExceeded,
        bottomCard: bottomCardReducer,
        browsePath: browsePathReducer,
        browser,
        catalog: catalogReducer,
        catalogAd: catalogAdReducer,
        catalogAnnouncement: catalogAnnouncementReducer,
        catalogDetail: catalogDetailReducer,
        catalogItems: catalogItemsReducer,
        catalogRegistration: catalogRegistrationReducer,
        category: categoryReducer,
        categoryCatalogCards,
        categoryRelatedSearches,
        categoryRelatedSearchTiles: categoryRelatedSearchTilesReducer,
        certCapture,
        chairishSimilarItems: chairishSimilarItemsReducer,
        changePassword: changePasswordReducer,
        checkout: checkoutReducer,
        checkoutShippingQuote: checkoutShippingQuoteReducer,
        client: clientReducer,
        combinationCategory,
        completeAccount,
        config,
        confirmPassword: confirmPasswordReducer,
        consent: consentReducer,
        consignment,
        console,
        consoleBranding,
        consoleMessages,
        conversation: conversationReducer,
        conversationFolders: conversationFoldersReducer,
        conversationMessages: conversationMessagesReducer,
        cookies,
        createAccount: createAccountReducer,
        createAccountEmailVerification: createAccountEmailVerificationReducer,
        creditCard: creditCardReducer,
        curatedCollection,
        currency,
        deleteAccount: deleteAccountReducer,
        discover,
        emailList: emailListReducer,
        errors,
        favoriteItems: favoriteItemsReducer,
        featuredCatalogs,
        featuredCategories,
        feedback,
        followSeller,
        forgotPassword,
        geoArea,
        healthcheck,
        helpModal: helpModalReducer,
        home: homeReducer,
        imageSearch: imageSearchReducer,
        intl,
        // item,
        itemCarousel: itemCarouselReducer,
        itemDetail,
        itemFacets,
        itemRemovalResponse,
        itemSummary: itemSummaryReducer,
        landingPage,
        listingAgent,
        liveBid,
        liveCatalogStatus,
        liveItemStatus,
        login: loginReducer,
        makeOffer,
        modal,
        notification,
        otherCategories,
        pagination: paginationCookiesReducer,
        passwordReset,
        payment,
        persist: combineReducers({
            headerSearchDropdown,
            pagination: paginationLocalStorageReducer,
        }),
        phoneVerify: phoneVerifyReducer,
        priceResults: priceResultsReducer,
        processingFee,
        recentItem,
        recommendedItem,
        recommendedSimilarItems: recommendedSimilarItemsReducer,
        registerForCatalog,
        relatedSearches: relatedSearchesReducer,
        reseller,
        retractBid: retractBidReducer,
        review,
        reviewStatus,
        savedItemCount,
        savedItems: savedItemsReducer,
        savedSearch,
        saveSearch,
        search: searchReducer,
        searchExclusions: searchExclusionsReducer,
        searchPromoted,
        searchSuggestions: searchSuggestionsReducer,
        seller: sellerReducer,
        sellerCatalogCounts,
        sellerDetail,
        sellerEndedCatalogs,
        sellerFollowerCount,
        sellerPreviewCatalogs,
        sellerRatings,
        sellerRecordResults,
        sellerUpcomingCatalogs,
        sendSellerMessage,
        setPassword,
        shipping: shippingReducer,
        shippingQuote: shippingQuoteReducer,
        shouldLogin: shouldLoginReducer,
        signifyd,
        similarItemAlerts: similarItemAlertsReducer,
        similarItemsGroups: similarItemsGroupsReducer,
        similarItems,
        similarItemsCategories,
        smsAlerts,
        storefront: storefrontReducer,
        subcategoryTiles,
        suggestedCategoryTiles,
        taxes,
        topRatedSellers,
        trustMetrics,
        twoStepAuthentication: twoStepAuthenticationReducer,
        upcomingCatalogs,
        upcomingItems: upcomingItemsReducer,
        updateAccount,
        user: userReducer,
        userBidPlacedItems: userBidPlacedItemsSliceReducer,
        video,
        viewData,
        waitingForApproval,
        whiteLabel,
        windowSize,
        wonItems: wonItemsReducer,
        wonItemsCount: wonItemsCountReducer,
    });
